import { Card, Typography, Grid, Box, CardContent, Stack } from '@mui/material';
import AngleRightIcon from 'common/customIcons/angle_right';
import EditIcon from 'common/customIcons/edit';
import MDButton from 'components/MDButton';
import CommonLayout from 'layouts/DashboardLayout';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { fetchStoreDetails, updateStoreBranch } from '../../../redux/slices/storeSlice';
import colors from 'assets/theme/base/colors';
import { formatUsPhoneNumber } from 'utilities/common';
import StoreOrders from 'components/stores/Orders';
import StoreProducts from 'components/stores/Products';
import StoreMembers from 'components/stores/Members';
import StoreApprovals from 'components/stores/Approvals';
import { openSnackbar, uploadImage } from '../../../redux/slices/common';
import { isValidFileExtension } from 'utilities/common';
import { getData } from 'utilities/common';
import { hasSectionPermission } from 'utilities/common';
import { getPermissionsByMode } from 'utilities/common';

export default function StoreDetails() {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState('storeDetails');
  const [searchParams] = useSearchParams(search);
  const { grey } = colors;
  const { storeDetails = {} } = useSelector(state => state.stores) || {};
  const {
    storeName,
    firstName,
    lastName,
    phone,
    address = {},
    email,
    status,
  } = storeDetails;

  const { storeId } = useParams();
  let localUser = getData('login-user-info')
  if (localUser) {
    localUser = JSON.parse(localUser)
  }

  const adminMode = localStorage.getItem("adminsMode") ? JSON.parse(localStorage.getItem("adminsMode")) : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);

  const canCreate = hasSectionPermission(permissionsForMode, "POS", "Create");
  const canEdit = hasSectionPermission(permissionsForMode, "POS", "Edit/Update");
  const canView = hasSectionPermission(permissionsForMode, "POS", "View Only");
  const canDelete = hasSectionPermission(permissionsForMode, "POS", "Delete");
  useEffect(() => {
    if (storeId) {
      dispatch(fetchStoreDetails(storeId));
    }
  }, [storeId]);


  useEffect(() => {
    if (search) {
      setCurrentTab(searchParams.get('filter'));
    }
  }, [search]);

  const showMessage = (message, errortype) => {
    if (errortype === 'success') {
      dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
    }
    if (errortype === 'error') {
      dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
    }
  };

  const uploadKMLHandler = async (e, branchId) => {
    if (e.target.files.length) {
      if (!isValidFileExtension(e.target.files[0], '.kml')) {
        showMessage('Upload only KML file', 'error');
        return;
      }
      const file = e.target.files[0];
      const body = new FormData();
      body.append('filePath', file);
      body.append('storeId', storeId);
      try {
        const res = await dispatch(uploadImage(body)).unwrap();

        const updateData = {
          kml: res?.fileUrl,
          storeId: storeId
        };

        await dispatch(updateStoreBranch({ data: updateData, branchId }))
          .unwrap().then((res) => {
            dispatch(fetchStoreDetails(storeId));
            showMessage(res?.message, 'success');
          });

      } catch (error) {
      }
      e.target.value = '';
    }
  };

  return (<>
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }}>

        <Link to='/stores'>
          <Box display='flex' alignItems='center' color='text.main' lineHeight={1} mb={3.5}>
            <AngleRightIcon fontSize='14px' />
            <Typography variant='h6' fontWeight='semibold' ml={.75}>Stores</Typography>
          </Box>
        </Link>
        {
          currentTab === 'storeOrders' ?
            <StoreOrders storeId={storeId} /> :

            currentTab === 'storeProducts' ?
              <StoreProducts /> :

              currentTab === 'storeMembers' || currentTab.includes('storeMembers') ?
                <StoreMembers storeId={storeId} /> :

                currentTab === 'storeApprovals' ?
                  <StoreApprovals storeId={storeId} canCreate={canCreate} canEdit={canEdit} canView={canView} canDelete={canDelete} /> :

                  <Grid container spacing={2} mb={4}>
                    <Grid item xs={12} md={12} lg={12}>
                      <Stack direction='row' alignItems='center' justifyContent='space-between'>
                        <Typography variant='h5'>Store Details</Typography>
                        <Stack direction='row' alignItems='center' gap={.75} lineHeight={1} p={.75} borderRadius={2} >
                          {
                            status === 'active' && <MDButton
                              sx={{ minWidth: '92px', borderRadius: 1.75, p: .25, fontWeight: 500 }}
                              style={{ opacity: 1 }}
                              type='button'
                              variant={status === 'active' ? 'contained' : 'text'}
                              color={status === 'active' ? 'success' : 'dark'}
                              size='small'
                              disabled
                            >
                              Active
                            </MDButton>
                          }

                          {
                            status === 'inactive' && <MDButton
                              sx={{ minWidth: '92px', borderRadius: 1.75, p: .25, fontWeight: 500 }}
                              type='button'
                              variant={status === 'inactive' ? 'contained' : 'text'}
                              color={status === 'inactive' ? 'error' : 'dark'}
                              size='small'
                              style={{ opacity: 1 }}
                              disabled
                            >
                              Inactive
                            </MDButton>
                          }

                          {
                            status === 'pending' && <MDButton
                              sx={{ minWidth: '92px', borderRadius: 1.75, p: .25, fontWeight: 500 }}
                              type='button'
                              variant={status === 'pending' ? 'contained' : 'text'}
                              // color={status === 'pending' ? 'error' : 'dark'}
                              size='small'
                              style={{ opacity: 1, backgroundColor: '#8E0CF5', color: '#ffffff' }}
                              disabled
                            >
                              Pending
                            </MDButton>
                          }
                          {
                            canEdit &&
                            <MDButton
                              type='button'
                              variant='outlined'
                              color='dark'
                              size='small'
                              startIcon={<EditIcon width='14px' height='14px' />}
                              sx={{ borderRadius: 30 }}
                              onClick={() => navigate(`/stores/${storeId}`)}
                            >
                              Edit
                            </MDButton>
                          }
                        </Stack>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={12} lg={12}>
                      <Card variant='detailsCard' sx={{ height: '100%', overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, md: 3.5 }, pb: 0 }}>
                          <Grid container alignItems='center' spacing={3.5} mb={1}>
                            <Grid item xs={12} md={4}>
                              <Typography variant='body2' color={grey[600]}>Store ID</Typography>
                              <Typography variant='h6' fontWeight='medium' sx={{ textTransform: 'capitalize' }}>{storeId}</Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant='body2' color={grey[600]}>Store Name</Typography>
                              <Typography variant='h6' fontWeight='medium' sx={{ textTransform: 'capitalize' }}>{storeName?.toLowerCase()}</Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant='body2' color={grey[600]}>Store Address</Typography>
                              <Typography variant='h6' fontWeight='medium'>{address.address}</Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant='body2' color={grey[600]}>Contact Name</Typography>
                              <Typography variant='h6' fontWeight='medium' sx={{ textTransform: 'capitalize' }}>{firstName?.toLowerCase() + ' ' + lastName?.toLowerCase()}</Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant='body2' color={grey[600]}>Email</Typography>
                              <Typography variant='h6' fontWeight='medium'>{email}</Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant='body2' color={grey[600]}>Phone</Typography>
                              <Typography variant='h6' fontWeight='medium'>{phone ? formatUsPhoneNumber(phone) : '-'}</Typography>
                            </Grid>

                            <Grid item xs={12} md={4}>
                              <Typography variant='body2' color={grey[600]}>City</Typography>
                              <Typography variant='h6' fontWeight='medium'>{address.city}</Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant='body2' color={grey[600]}>State</Typography>
                              <Typography variant='h6' fontWeight='medium'>{address.state}</Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant='body2' color={grey[600]}>Zipcode</Typography>
                              <Typography variant='h6' fontWeight='medium'>{address.zipcode}</Typography>
                            </Grid>
                            {/* {
                              !isStilo &&
                              <>
                                <Grid item xs={12} md={4}>
                                  <Typography variant='body2' color={grey[600]}>Permission to view user details</Typography>
                                  <Typography variant='h6' fontWeight='medium'>{isViewUser ? 'Yes' : 'No'}</Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <Typography variant='body2' color={grey[600]} className='d-flex' >Subscription  <div className='text-color font-w-600 ml-1'> {valiDateFrom ? (`(Valid Upto ${formatTimestampToWithoutTime(valiDateTo)})`) : '(Lifetime)'}</div> </Typography>
                                  <Typography variant='h6' fontWeight='medium' color={orange[600]} className={`${subscription === 'Paid' ? 'paid-badge' : ''}`}><p>{subscription}</p></Typography>
                                </Grid>
                              </>
                            } */}

                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
        }
      </Box>
    </CommonLayout>
  </>);
}